import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Select, MenuItem, FormControl, InputLabel, Alert, CircularProgress,
  IconButton, Tooltip, Paper,
} from '@mui/material';
import { Add, VerifiedUser, Delete, Edit } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import millingApi from '../../../services/millingApi';

const statusColor: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  pass: 'success', fail: 'error', conditional_release: 'warning', pending: 'default',
};

const AflatoxinList: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await millingApi.getAflatoxinRecords(statusFilter ? { status: statusFilter } : {});
      setRows(res.data ?? res);
    } catch { setError('Failed to load records'); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleCertify = async (id: string) => {
    const by = window.prompt('Certified by:');
    if (by === null) return;
    try { await millingApi.certifyAflatoxin(id, by); load(); }
    catch { setError('Certify failed'); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this record?')) return;
    try { await millingApi.deleteAflatoxinRecord(id); load(); }
    catch { setError('Delete failed'); }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>Aflatoxin Monitoring</Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="pass">Pass</MenuItem>
            <MenuItem value="fail">Fail</MenuItem>
            <MenuItem value="conditional_release">Conditional</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/milling/aflatoxin/new')}>
          New Test
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Sample #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Method</TableCell>
                <TableCell align="right">Result (ppb)</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Certified By</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={9} align="center">No records found</TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.sampleNumber}</TableCell>
                  <TableCell>{r.sampleDate}</TableCell>
                  <TableCell>{r.sampleSource?.replace('_', ' ')}</TableCell>
                  <TableCell>{r.referenceNumber || '-'}</TableCell>
                  <TableCell>{r.testMethod || '-'}</TableCell>
                  <TableCell align="right">
                    {r.resultPpb != null ? (
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 'bold', color: r.resultPpb > 10 ? 'error.main' : 'success.main' }}
                      >
                        {r.resultPpb}
                      </Typography>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip label={r.status} size="small" color={statusColor[r.status] ?? 'default'} />
                  </TableCell>
                  <TableCell>{r.certifiedBy || '-'}</TableCell>
                  <TableCell>
                    <Tooltip title="Certify">
                      <IconButton size="small" onClick={() => handleCertify(r.id)}>
                        <VerifiedUser fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => navigate(`/milling/aflatoxin/${r.id}/edit`)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default AflatoxinList;
